import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardHeader,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell as TableCellOriginal,
  TableContainer as TableContainerOriginal,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { HeadCell } from '@pflegenavi/web-components';
import {
  TableFillerTextStyled,
  TableNoData,
  TableSkeleton,
  OneLineTypography,
  Label,
} from '@pflegenavi/web-components';
import {
  IconTooltip,
  EnlargeImageGallery,
} from '@pflegenavi/transaction-components';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useFormatting, useLocale } from '@pflegenavi/frontend/localization';
import type { ReceiptPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { ReceiptState } from '@pflegenavi/shared/api';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { TransactionTypeLogo } from '../general/TransactionTypeLogo';
import { PendingIconOverlay } from '../common/PendingIconOverlay';

import NoDataImage from '../../assets/undraw_no_records.svg';

enum LatestPendingReceiptsColumns {
  type = 'type',
  date = 'date',
  receipt = 'receipt',
  amount = 'amount',
}

interface LatestPendingReceiptsViewProps {
  isLoading: boolean;
  data: ReceiptPhoenix[];
  onReceiptClick: (receipt: ReceiptPhoenix) => void;
  receiptImageUrls?: string[];
  imageUrlsLoading?: boolean;
  imageUrlToTypeMap?: UrlTypeNameMapping;
  receiptImageIds?: string[];
  onReceiptImageClick: (imageIds: string[]) => void;
  onCloseImageGallery: () => void;
  receiptState?: ReceiptState;
}

export const LatestPendingReceiptsView: FC<LatestPendingReceiptsViewProps> = ({
  isLoading,
  data,
  onReceiptClick,
  receiptImageUrls = [],
  imageUrlsLoading = false,
  imageUrlToTypeMap = new Map(),
  receiptImageIds,
  onReceiptImageClick,
  onCloseImageGallery,
  receiptState = ReceiptState.Pending,
}) => {
  const { t } = useTranslation();

  // Helper function to get appropriate translation keys based on receipt state
  const getTranslationKeys = () => {
    if (receiptState === ReceiptState.AwaitingSettlement) {
      return {
        title: 'dashboard.awaiting-settlement-receipts',
        noData: 'dashboard.no-awaiting-settlement-receipts',
        status: 'receipts.awaiting-settlement-status',
      };
    }

    // Default to pending
    return {
      title: 'dashboard.pending-receipts',
      noData: 'dashboard.no-pending-receipts',
      status: 'receipts.pending-status',
    };
  };

  const translationKeys = getTranslationKeys();

  const TABLE_HEAD = useMemo<Array<HeadCell<LatestPendingReceiptsColumns>>>(
    () => [
      {
        id: LatestPendingReceiptsColumns.type,
        label: t('common.type'),
        align: 'left',
      },
      {
        id: LatestPendingReceiptsColumns.date,
        label: t('common.date'),
        align: 'left',
      },
      {
        id: LatestPendingReceiptsColumns.receipt,
        label: t('common.receipt'),
        align: 'center',
      },
      {
        id: LatestPendingReceiptsColumns.amount,
        label: t('common.amount'),
        align: 'right',
      },
    ],
    [t]
  );

  return (
    <Card sx={{ position: 'relative' }} raised>
      <CardHeader
        title={t(translationKeys.title)}
        sx={{ mb: 3 }}
        slotProps={{
          title: {
            variant: 'h5',
            sx: { fontWeight: 600 },
          },
        }}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {TABLE_HEAD.map((head) => (
                <ThCell key={head.id} align={head.align}>
                  {head.label}
                </ThCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading &&
              Array.from(Array(3).keys()).map((row: number) => (
                <TableSkeleton key={`row-${row}`} colSpan={TABLE_HEAD.length} />
              ))}

            {!isLoading && data?.length === 0 && (
              <TableNoData colSpan={TABLE_HEAD.length} isNotFound>
                <img
                  alt="empty content"
                  src={NoDataImage}
                  style={{ marginBottom: 25 }}
                  height={180}
                  width={220}
                />
                <TableFillerTextStyled>
                  {t(translationKeys.noData)}
                </TableFillerTextStyled>
              </TableNoData>
            )}

            {!isLoading &&
              data.map((receipt) => (
                <PendingReceiptRow
                  key={receipt.id}
                  receipt={receipt}
                  handleShowReceiptImage={() => {
                    if (
                      receipt.receipt_image_ids &&
                      receipt.receipt_image_ids.length > 0
                    ) {
                      onReceiptImageClick(receipt.receipt_image_ids);
                    }
                  }}
                />
              ))}

            {!isLoading && (
              <EnlargeImageGallery
                loading={imageUrlsLoading}
                open={Boolean(receiptImageIds)}
                handleClose={onCloseImageGallery}
                initialSelectedIndex={0}
                imageUrls={receiptImageUrls}
                imageUrlToTypeMap={imageUrlToTypeMap}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

interface PendingReceiptRowProps {
  receipt: ReceiptPhoenix;
  handleShowReceiptImage: () => void;
}

const PendingReceiptRow: FC<PendingReceiptRowProps> = ({
  receipt,
  handleShowReceiptImage,
}) => {
  const theme = useTheme();
  const { fCurrencyCents } = useFormatting();
  const locale = useLocale();
  const { t } = useTranslation();

  const receiptType = receipt.receipt_type?.name;
  const receiptTypeLabel = receiptType
    ? t(`receiptType.${receiptType}`)
    : t('receipts.general-receipt');
  const serviceProvider = receipt.service_provider?.name || '--';

  // Helper function to get status text based on receipt state
  const getStatusText = () => {
    if (receipt.receipt_state === ReceiptState.Pending) {
      return t('receipts.pending-status');
    }
    if (receipt.receipt_state === ReceiptState.AwaitingSettlement) {
      return t('receipts.awaiting-settlement-status');
    }
    return t('receipts.pending-status'); // fallback
  };

  const amountTooltipTitle = useMemo(() => {
    let title;

    if (
      receipt.additional_fee_amount !== 0 &&
      receipt.included_fee_amount !== 0
    ) {
      title = t('tooltip.included-and-additional-fee', {
        additionalFeeAmount: fCurrencyCents(receipt.additional_fee_amount),
        includedFeeAmount: fCurrencyCents(receipt.included_fee_amount),
      });
    } else if (receipt.additional_fee_amount !== 0) {
      title = t('tooltip.additional-fee', {
        additionalFeeAmount: fCurrencyCents(receipt.additional_fee_amount),
      });
    } else if (receipt.included_fee_amount !== 0) {
      title = t('tooltip.included-fee', {
        includedFeeAmount: fCurrencyCents(receipt.included_fee_amount),
      });
    }

    return title;
  }, [fCurrencyCents, t, receipt]);

  const renderReceiptIcon = useMemo(() => {
    if (
      receipt.receipt_image_ids &&
      receipt.receipt_image_ids.length > 0 &&
      !receipt.receipt_not_available
    ) {
      return (
        <IconTooltip
          title={t('tooltip.show-receipt-image')}
          color={theme.palette.success.main}
          icon="check"
          onClick={handleShowReceiptImage}
        />
      );
    }

    return (
      <IconTooltip
        title={t('tooltip.no-image-available')}
        color={theme.palette.grey['400']}
        icon="remove"
      />
    );
  }, [
    receipt.receipt_image_ids,
    receipt.receipt_not_available,
    handleShowReceiptImage,
    t,
    theme.palette.grey,
    theme.palette.success.main,
  ]);

  return (
    <TableRow
      sx={{
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.grey[500_24],
        borderBottomWidth: 0,
      }}
    >
      <TableCell align="left">
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <TransactionTypeLogo logoType={receiptType} />
            <PendingIconOverlay />
          </Box>
          <Stack
            direction="column"
            sx={{
              justifyContent: 'center',
              alignItems: 'flex-start',
              ml: 1.5,
            }}
          >
            <OneLineTypography
              variant="body1"
              sx={{
                fontWeight: 600,
                lineHeight: 1.2,
                maxWidth: 230,
              }}
            >
              {receiptTypeLabel} ({getStatusText()})
            </OneLineTypography>
            <OneLineTypography
              variant="body2"
              sx={{ color: 'grey.500', maxWidth: 230 }}
              noWrap
            >
              {serviceProvider}
            </OneLineTypography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2">
          {new Date(receipt.receipt_date).toLocaleDateString(
            locale,
            YEAR_MONTH_DAY_SHORT_FORMAT
          )}
        </Typography>
      </TableCell>
      <TableCell
        align="center"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {renderReceiptIcon}
      </TableCell>
      <TableCell align="right">
        {receipt.additional_fee_amount !== 0 ||
        receipt.included_fee_amount !== 0 ? (
          <Tooltip arrow placement="left" title={amountTooltipTitle}>
            <span>
              <Label variant="ghost" color="info">
                {fCurrencyCents(
                  -receipt.amount - receipt.additional_fee_amount
                )}
              </Label>
            </span>
          </Tooltip>
        ) : (
          <Label variant="ghost" color="info">
            {fCurrencyCents(-receipt.amount - receipt.additional_fee_amount)}
          </Label>
        )}
      </TableCell>
    </TableRow>
  );
};

const TableCell = styled(TableCellOriginal)`
  padding: 12px;
`;

const ThCell = styled(TableCell)(({ theme }) =>
  theme.unstable_sx({ color: 'grey.600' })
);

const TableContainer = styled(TableContainerOriginal)(({ theme }) =>
  theme.unstable_sx({
    maxWidth: '96%',
    height: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  })
);
